import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ClassMembersPage from '../pages/ClassMembersPage';
import type { Grade } from '../types/domain';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';

interface ClassMembersSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  grades: Grade[];
  /** 모달을 열 때 기본으로 선택할 클래스 (없으면 첫 클래스) */
  initialGradeId?: string | null;
}

const ClassMembersSettingsModal: React.FC<ClassMembersSettingsModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  grades,
  initialGradeId = null,
}) => {
  const [activeGradeId, setActiveGradeId] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const saveActionsRef = useRef<{ save: () => Promise<void>; discard?: () => void } | null>(null);

  const sortedGrades = useMemo(
    () => [...grades].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [grades]
  );

  const effectiveGradeId = useMemo(() => {
    if (activeGradeId && sortedGrades.some((g) => g.id === activeGradeId)) return activeGradeId;
    const preferred = (initialGradeId ?? '').trim();
    if (preferred && sortedGrades.some((g) => g.id === preferred)) return preferred;
    return sortedGrades[0]?.id ?? '';
  }, [activeGradeId, initialGradeId, sortedGrades]);

  useEffect(() => {
    if (!isOpen) {
      setIsDirty(false);
      setActiveGradeId('');
      setCloseConfirmOpen(false);
      setClosing(false);
      return;
    }
    setActiveGradeId(effectiveGradeId);
  }, [isOpen, effectiveGradeId]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const finishClose = useCallback(() => {
    setCloseConfirmOpen(false);
    setClosing(false);
    setIsDirty(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (closing) return;
    if (isDirty) {
      setCloseConfirmOpen(true);
      return;
    }
    finishClose();
  }, [closing, finishClose, isDirty]);

  const discardAndClose = useCallback(() => {
    if (closing) return;
    saveActionsRef.current?.discard?.();
    finishClose();
  }, [closing, finishClose]);

  const saveAndClose = useCallback(async () => {
    if (closing) return;
    setClosing(true);
    try {
      await saveActionsRef.current?.save();
      finishClose();
    } catch {
      setClosing(false);
    }
  }, [closing, finishClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (closeConfirmOpen) {
        if (!closing) setCloseConfirmOpen(false);
        return;
      }
      requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeConfirmOpen, closing, requestClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="cp-class-settings-modal-root cp-class-members-settings-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-class-members-settings-modal-title"
    >
      <div className="cp-class-settings-modal-dim" aria-hidden onClick={requestClose} />
      <div
        className="cp-class-settings-modal-panel cp-class-members-settings-modal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-class-settings-modal-header">
          <div className="cp-class-settings-modal-heading">
            <h2 id="cp-class-members-settings-modal-title" className="cp-class-settings-modal-title">
              클래스 멤버 설정
            </h2>
          </div>
          <div className="cp-class-members-settings-modal-header-actions">
            <button
              type="button"
              className="cp-btn-save-all"
              onClick={() => {
                // 번호 중복 등으로 저장이 막히면 save()가 reject 한다(안내는 페이지 토스트가 표시).
                void saveActionsRef.current?.save()?.catch(() => undefined);
              }}
              disabled={!isDirty || closing}
            >
              {isDirty ? '저장하기' : '변경사항 없음'}
            </button>
            <button
              type="button"
              className="cp-class-settings-modal-close"
              onClick={requestClose}
              aria-label="닫기"
              disabled={closing}
            >
              <img src="/modal-close.svg" alt="" width={18} height={18} />
            </button>
          </div>
        </div>
        <div className="cp-class-settings-modal-body cp-class-members-settings-modal-body">
          {sortedGrades.length > 0 ? (
            <div
              className="cp-annual-progress-tabs cp-class-members-settings-modal-tabs"
              role="tablist"
              aria-label="클래스 탭"
            >
              {sortedGrades.map((grade) => {
                const active = grade.id === effectiveGradeId;
                const parsed = parseGradeDisplayName(grade.name);
                const label = parsed.title || grade.name;
                return (
                  <button
                    key={grade.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={
                      'cp-grade-track-class-block cp-class-members-settings-modal-class-tab' +
                      (active ? ' is-active' : ' is-nav-muted')
                    }
                    onClick={() => setActiveGradeId(grade.id)}
                  >
                    <span className="cp-grade-track-class-block-label">{label}</span>
                    {parsed.grades.map((num) => (
                      <span key={num} className="class-grade-chip-display">
                        {num}
                      </span>
                    ))}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="cp-class-members-settings-modal-tabs-spacer" aria-hidden />
          )}
          <div className="cp-class-settings-modal-inner cp-class-members-settings-modal-inner">
            <ClassMembersPage
              workspaceId={workspaceId}
              grades={grades}
              currentGradeId={effectiveGradeId}
              embeddedInModal
              onDirtyChange={setIsDirty}
              saveActionsRef={saveActionsRef}
            />
          </div>
        </div>
      </div>

      {closeConfirmOpen ? (
        <div
          className="cp-modal-backdrop cp-class-members-settings-close-confirm-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-class-members-settings-close-confirm-title"
          onClick={() => {
            if (!closing) setCloseConfirmOpen(false);
          }}
        >
          <div className="cp-modal cp-leave-modal-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="cp-class-members-settings-close-confirm-title" className="cp-leave-modal-title">
              변경사항이 저장되지 않았습니다
            </h3>
            <p className="cp-leave-modal-desc">저장하지 않고 나가시겠습니까?</p>
            <div className="cp-modal-footer cp-leave-modal-actions">
              <button
                type="button"
                className="cp-btn-secondary"
                onClick={discardAndClose}
                disabled={closing}
              >
                저장하지 않고 나가기
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void saveAndClose()}
                disabled={closing}
              >
                {closing ? '저장 중…' : '저장 하기'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body
  );
};

export default ClassMembersSettingsModal;
